import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { Eye, LogOut, Save } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { AdminContentProvider, useAdminContent } from "@/context/AdminContentContext";
import { Logo } from "@/components/site/Logo";
import { formatDate } from "@/lib/format";

const SECTIONS = [
  { to: "/gestao/inicio", label: "Página inicial" },
  { to: "/gestao/textos", label: "Textos do site" },
  { to: "/gestao/tema", label: "Cores & letra" },
  { to: "/gestao/manutencao", label: "Manutenção" },
  { to: "/gestao/candidaturas", label: "Candidaturas" },
  { to: "/gestao/informacoes", label: "Informações" },
  { to: "/gestao/comunidade", label: "Comunidade Discord" },
  { to: "/gestao/loja", label: "Loja" },
  { to: "/gestao/precario", label: "Preçário" },
  { to: "/gestao/ligacoes", label: "Ligações & IDs" },
  { to: "/gestao/media", label: "Imagens & vídeo" },
  { to: "/gestao/regras", label: "Regras" },
  { to: "/gestao/conta", label: "Conta" },
  { to: "/gestao/equipa", label: "Equipa", ownerOnly: true },
  { to: "/gestao/atividade", label: "Atividade" },
];

function TopBar() {
  const { meta, dirty, busy, save } = useAdminContent();
  const { logout, user } = useAuth();
  const [confirmExit, setConfirmExit] = useState(false);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const onLogout = () => {
    if (dirty) setConfirmExit(true);
    else logout();
  };

  const saveAndLogout = async () => {
    const ok = await save();
    setConfirmExit(false);
    if (ok) logout();
  };

  return (
    <header className="sticky top-0 z-40 border-b border-eden-border bg-eden-bg/95 backdrop-blur" data-testid="gestao-topbar">
      <AlertDialog open={confirmExit} onOpenChange={setConfirmExit}>
        <AlertDialogContent className="border-eden-border bg-eden-surface" data-testid="exit-reminder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-eden-text">Tens alterações por guardar</AlertDialogTitle>
            <AlertDialogDescription className="text-eden-secondary">Se saíres agora, as alterações que fizeste perdem-se. Queres guardar antes de sair?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-eden-border bg-transparent text-eden-text hover:bg-eden-elevated" data-testid="exit-reminder-cancel">Continuar a editar</AlertDialogCancel>
            <button type="button" onClick={() => { setConfirmExit(false); logout(); }} className="btn-ghost h-10" data-testid="exit-reminder-leave">Sair sem guardar</button>
            <AlertDialogAction onClick={saveAndLogout} className="bg-eden-orange text-eden-bg hover:bg-eden-orangeHover" data-testid="exit-reminder-publish">Guardar e sair</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-5 py-3">
        <Logo className="h-7" textClass="text-base" />
        <span className="hidden font-mono text-[11px] uppercase tracking-widest text-eden-muted sm:inline">Gestão</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="hidden text-xs text-eden-muted md:inline" data-testid="gestao-last-change">
            {dirty ? <span className="rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-amber-300">Alterações por guardar</span> : meta.published_at ? `No site desde ${formatDate(meta.published_at)}` : ""}
          </span>
          <a href="/" target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="gestao-view-site-button"><Eye size={16} /> Ver site</a>
          <button type="button" onClick={() => save()} disabled={busy || !dirty} className="btn-primary h-10" data-testid="gestao-save-button"><Save size={16} /> Guardar</button>
          <button type="button" onClick={onLogout} className="btn-ghost" aria-label={`Terminar sessão (${user?.email})`} data-testid="gestao-logout-button"><LogOut size={16} /></button>
        </div>
      </div>
    </header>
  );
}

function Shell() {
  const { draft } = useAdminContent();
  const { user } = useAuth();
  const sections = SECTIONS.filter((s) => !s.ownerOnly || user?.role === "owner");
  return (
    <div className="min-h-screen bg-eden-bg text-eden-text">
      <TopBar />
      <div className="mx-auto grid max-w-[1400px] gap-8 px-5 py-8 lg:grid-cols-[220px_1fr]">
        <nav aria-label="Secções do painel">
          <ul className="flex gap-1 overflow-x-auto lg:flex-col lg:border-l lg:border-eden-border" data-testid="gestao-sidebar">
            {sections.map((s) => (
              <li key={s.to} className="shrink-0">
                <NavLink to={s.to} className={({ isActive }) => `block whitespace-nowrap rounded-sm px-3 py-2 text-sm lg:-ml-px lg:rounded-none lg:border-l-2 ${isActive ? "border-eden-orange bg-eden-surface text-eden-text lg:bg-transparent" : "border-transparent text-eden-secondary hover:text-eden-text"}`} data-testid={`gestao-nav-${s.to.split("/").pop()}`}>
                  {s.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <p className="mt-6 hidden text-xs text-eden-muted lg:block" data-testid="gestao-published-at">Tudo o que guardas fica logo visível no site.</p>
        </nav>
        <div className="min-w-0 space-y-6">{draft ? <Outlet /> : <p className="text-sm text-eden-muted">A carregar…</p>}</div>
      </div>
    </div>
  );
}

export default function GestaoLayout() {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-eden-bg text-sm text-eden-muted">A verificar sessão…</div>;
  if (user === false) return <Navigate to="/gestao" replace />;
  return (
    <AdminContentProvider>
      <Shell />
    </AdminContentProvider>
  );
}
